import React, { useState, useEffect } from 'react';
import WalletGroupManager from '../components/WalletGroupManager';
import WalletList from '../components/WalletList';
import WalletDetail from '../components/WalletDetail';
import TradingBot from '../components/TradingBot';
import Navbar from '../components/Navbar';
import Whitelist from '../components/Whitelist';
import { useToken } from '../contexts/TokenContext';
import { Wallet, Shield } from 'lucide-react'
import api from '../services/api';

const WalletGroupsPage = () => {
  const { tokenId, tokenData, updateTokenData } = useToken();
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [selectedWallet, setSelectedWallet] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [groups, setGroups] = useState([]);
  const [activeTab, setActiveTab] = useState('wallets');

  // Collapsible section states for mobile/tablet
  const [isGroupsCollapsed, setIsGroupsCollapsed] = useState(false);
  const [isTradingBotCollapsed, setIsTradingBotCollapsed] = useState(false);
  const [isWalletListCollapsed, setIsWalletListCollapsed] = useState(false);
  const [isWalletDetailCollapsed, setIsWalletDetailCollapsed] = useState(false);
  
  // Fetch token data if we have tokenId but no complete tokenData
  useEffect(() => {
    const fetchTokenData = async () => {
      if (tokenId && (!tokenData || !tokenData.mint_address)) {
        try {
          console.log('🔍 Fetching token data for:', tokenId);
          let response;
          try {
            response = await api.get(`/available-tokens/${tokenId}`);
          } catch (error) {
            console.log('Available tokens endpoint failed, trying tokens endpoint');
            response = await api.get(`/tokens/${tokenId}`);
          }
          
          console.log('🔍 Fetched token data:', response.data);
          updateTokenData(response.data);
        } catch (error) {
          console.error('Error fetching token data:', error);
        }
      }
    };
    
    fetchTokenData();
  }, [tokenId, tokenData, updateTokenData]);
  
  // Force refresh of components
  const handleRefresh = (updatedWallet) => {
    // If we have an updated wallet, update the selected wallet
    if (updatedWallet && selectedWallet && updatedWallet.id === selectedWallet.id) {
      setSelectedWallet(updatedWallet);
    }
    setRefreshTrigger(prev => prev + 1);
  };

  // Handle group selection
  const handleGroupSelect = (groupId) => {
    setSelectedGroupId(groupId);
  };

  // Handle refresh and update groups
  const handleRefreshWithGroups = (updatedWallet) => {
    // If we have an updated wallet, update the selected wallet
    if (updatedWallet && selectedWallet && updatedWallet.id === selectedWallet.id) {
      setSelectedWallet(updatedWallet);
    }
    setRefreshTrigger(prev => prev + 1);
  };

  // Handle groups update from WalletGroupManager
  const handleGroupsUpdate = (updatedGroups) => {
    if (updatedGroups) {
      setGroups(updatedGroups);
    }
  };

  // Get selected group name
  const getSelectedGroupName = () => {
    if (!selectedGroupId) return '';
    if (selectedGroupId === 'ungrouped') return 'Ungrouped Wallets';
    const group = groups.find(g => g.id === selectedGroupId);
    return group?.name || 'Selected Group';
  };
  
  return (
    <div className="flex flex-col h-screen bg-gray-50 dark:bg-gray-900 transition-colors">
      <div className="flex-1 p-3 sm:p-4 md:p-6 overflow-auto">
        {/* Header with Tabs */}
        <div className="mb-4 sm:mb-6">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-800 dark:text-gray-100 mb-3 sm:mb-4">Wallet Management</h1>

          {/* Tab Navigation */}
          <div className="border-b border-gray-200 dark:border-gray-700">
            <nav className="-mb-px flex space-x-4 sm:space-x-8">
              <button
                onClick={() => setActiveTab('wallets')}
                className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'wallets'
                    ? 'border-purple-500 text-purple-600 dark:text-purple-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                <div className="flex items-center space-x-1 xs:space-x-2">
                  <Wallet className="w-4 h-4" />
                  <span className="hidden xs:inline">Wallets</span>
                </div>
              </button>

              <button
                onClick={() => setActiveTab('whitelist')}
                className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'whitelist'
                    ? 'border-purple-500 text-purple-600 dark:text-purple-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                <div className="flex items-center space-x-1 xs:space-x-2">
                  <Shield className="w-4 h-4" />
                  <span className="hidden xs:inline">Whitelist</span>
                </div>
              </button>
            </nav>
          </div>
        </div>
        
        {/* Tab Content */}
        {activeTab === 'wallets' ? (
          <>
            {/* Top Section - Wallet Groups (Full Width) */}
            <div className="mb-4 sm:mb-6">
              {/* Collapsible Header for Groups Section (only on lg and below) */}
              <div className="lg:hidden mb-3">
                <button
                  onClick={() => setIsGroupsCollapsed(!isGroupsCollapsed)}
                  className="w-full flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-purple-500 rounded-full"></div>
                    <span className="font-medium text-gray-900 dark:text-gray-100">Wallet Groups</span>
                    <span className="text-sm text-gray-500 dark:text-gray-400">({groups.length})</span>
                  </div>
                  <svg
                    className={`w-5 h-5 text-gray-500 dark:text-gray-400 transition-transform ${isGroupsCollapsed ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              </div>

              <div className={`${isGroupsCollapsed ? 'hidden lg:block' : 'block'}`}>
                <WalletGroupManager
                  onGroupSelect={handleGroupSelect}
                  selectedGroupId={selectedGroupId}
                  onUpdateWallets={handleRefresh}
                  onGroupsUpdate={handleGroupsUpdate}
                  tokenId={tokenId}
                  key={`group-manager-${refreshTrigger}`}
                />
              </div>
            </div>

            {/* Trading Bot Section - Show when specific group is selected (not ungrouped or all) */}
            {selectedGroupId && selectedGroupId !== 'ungrouped' && selectedGroupId !== null && (
              <div className="mb-4 sm:mb-6">
                <TradingBot
                  selectedGroupId={selectedGroupId}
                  groupName={getSelectedGroupName()}
                />
              </div>
            )}

            {/* Bottom Section - Responsive Layout */}
            <div className="flex flex-col lg:flex-row gap-4 sm:gap-6 min-h-0 flex-1">
              {/* Left Column - Wallet List */}
              <div className="lg:w-1/2 flex flex-col min-h-0">
                {/* Collapsible Header for Wallet List (only on lg and below) */}
                <div className="lg:hidden mb-3">
                  <button
                    onClick={() => setIsWalletListCollapsed(!isWalletListCollapsed)}
                    className="w-full flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <Wallet className="w-4 h-4 text-blue-500" />
                      <span className="font-medium text-gray-900 dark:text-gray-100">
                        {selectedGroupId === null || selectedGroupId === undefined
                          ? 'All Wallets'
                          : selectedGroupId === 'ungrouped'
                          ? 'Ungrouped Wallets'
                          : groups.find(g => g.id === selectedGroupId)?.name || 'Wallets'}
                      </span>
                    </div>
                    <svg
                      className={`w-5 h-5 text-gray-500 dark:text-gray-400 transition-transform ${isWalletListCollapsed ? 'rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                </div>

                <div className={`${isWalletListCollapsed ? 'hidden lg:flex' : 'flex'} flex-col min-h-0 flex-1`}>
                  <WalletList
                    selectedGroupId={selectedGroupId}
                    onSelectWallet={setSelectedWallet}
                    selectedWallet={selectedWallet}
                    onUpdateGroups={handleRefresh}
                    tokenId={tokenId}
                    key={`wallet-list-${refreshTrigger}-${selectedGroupId}`}
                  />
                </div>
              </div>

              {/* Right Column - Wallet Details */}
              <div className={`lg:w-1/2 flex flex-col min-h-0 ${!selectedWallet ? 'hidden lg:flex' : ''}`}>
                {selectedWallet ? (
                  <>
                    {/* Collapsible Header for Wallet Details (only on lg and below) */}
                    <div className="lg:hidden mb-3">
                      <button
                        onClick={() => setIsWalletDetailCollapsed(!isWalletDetailCollapsed)}
                        className="w-full flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                      >
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 bg-indigo-500 rounded-full"></div>
                          <span className="font-medium text-gray-900 dark:text-gray-100">Wallet Details</span>
                          <span className="text-sm text-gray-500 dark:text-gray-400 truncate max-w-[120px]">
                            {selectedWallet.name || `Wallet ${selectedWallet.id}`}
                          </span>
                        </div>
                        <svg
                          className={`w-5 h-5 text-gray-500 dark:text-gray-400 transition-transform ${isWalletDetailCollapsed ? 'rotate-180' : ''}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                    </div>

                    <div className={`${isWalletDetailCollapsed ? 'hidden lg:flex' : 'flex'} flex-col min-h-0 flex-1`}>
                      <WalletDetail
                        wallet={selectedWallet}
                        onUpdate={handleRefresh}
                        tokenId={tokenId}
                        key={`wallet-detail-${selectedWallet.id}-${refreshTrigger}`}
                      />
                    </div>
                  </>
                ) : (
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 sm:p-8 text-center h-full flex items-center justify-center transition-colors">
                    <div>
                      <div className="text-gray-400 dark:text-gray-500 mb-4">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 sm:h-16 sm:w-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3 3v8a3 3 0 003 3z" />
                        </svg>
                      </div>
                      <h3 className="text-base sm:text-lg font-medium text-gray-700 dark:text-gray-300 mb-2">No Wallet Selected</h3>
                      <p className="text-sm sm:text-base text-gray-500 dark:text-gray-400">
                        Select a wallet from the list to view its details, or create a new one.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        ) : (
          /* Whitelist Tab Content */
          <Whitelist tokenId={tokenId} tokenData={tokenData} />
        )}
      </div>
    </div>
  );
};

export default WalletGroupsPage;